/**
 * WHAT EACH PASSPORT SECTION ACTUALLY MEASURES.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THIS IS NOT A SECURITY BOUNDARY, AND IT MUST NOT BE MISTAKEN FOR ONE
 * ═══════════════════════════════════════════════════════════════════════════
 * `./sectionAudience` decides WHO may see a section, fails closed, and is the
 * composition rule that exists so an undeclared section cannot leak to another
 * person. Nothing here widens or narrows it: a section hidden by the audience
 * contract stays hidden whatever this file says.
 *
 * This file answers a different question, on the subject's OWN page:
 *
 *   The person is being read through the Detail Specialist lens. Is this
 *   section TRUTHFUL about detail work, or would drawing it here attribute
 *   mechanical competence to somebody who washed cars?
 *
 * Because it is a truthfulness filter rather than a disclosure one, it fails
 * OPEN: an unclassified section renders in every lens. Failing closed would
 * silently delete a person's own evidence from their own Passport the moment
 * somebody added a component without updating a table — and the self-view
 * floor ("present, to the person, every substantive fact the Passport exposes
 * to any other human audience") is the thing that would break. Exhaustiveness
 * is enforced by a test instead of by a runtime blank page.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE CLASSIFICATION IS BY DATA, NOT BY NAME
 * ═══════════════════════════════════════════════════════════════════════════
 * Each `why` records what the section READS, traced on 2026-09-21. Two of the
 * conclusions are the opposite of what the component is called:
 *
 *   · `advisor-activity` is named for advisors and its own comment says it is
 *     ACTIVITY-BASED — it counts FOH actions performed by whoever performed
 *     them, including an OWNER or a TECHNICIAN doing front-counter work. It is
 *     still advisor EVIDENCE (the actions it counts are advisor actions), and
 *     that is why it belongs to the Advisor lens and not to "people with the
 *     advisor title".
 *
 *   · `time-signature` and `ticket-ledger` sound like technician sections and
 *     are not. They measure MINUTES and COMPLETED WORK, neither of which is a
 *     claim about mechanical skill. They are `mixed`, which means they render
 *     under every lens and carry a documented limitation where they cannot yet
 *     partition by the performed role.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE LIMITATION FIELD IS LOAD-BEARING
 * ═══════════════════════════════════════════════════════════════════════════
 * Phase 5 of the directive: "If an existing aggregate cannot distinguish
 * role-performed work because it was historically pre-multi-role: label or
 * document the limitation rather than falsely attributing it."
 *
 * `partitionable` is exactly that record. `frozen_role` means the section's
 * source carries `performed_work_role` and can be filtered honestly.
 * `not_partitioned` means it cannot today, and the UI is obliged to say so
 * rather than imply the number belongs to the selected lens.
 */

import type { PassportWorkLens } from './workLens';

// ───────────────────────────────────────────────────────────────────────────
// Vocabulary
// ───────────────────────────────────────────────────────────────────────────

export type PassportEvidenceDomain =
  /** True of the person regardless of the capacity they are read in. */
  | 'identity_general'
  /** Mechanical technician work. */
  | 'technician_evidence'
  /** Service-advisor work. */
  | 'advisor_evidence'
  /** Detail work. */
  | 'detail_evidence'
  /** Leadership behaviour. Never a work lens — see `workLens.ts`. */
  | 'leadership_evidence'
  /** Measures something real that spans capacities. Renders in every lens. */
  | 'mixed';

/** Can this section's source separate work by the role it was performed in? */
export type EvidencePartitionability =
  /** Source carries `performed_work_role` / `performed_work_family`. */
  | 'frozen_role'
  /** Cannot partition today. The limitation must be shown, not hidden. */
  | 'not_partitioned'
  /** Nothing to partition — the content is not role-performed work at all. */
  | 'not_applicable';

export interface PassportSectionEvidenceRule {
  readonly domain: PassportEvidenceDomain;
  readonly partitionable: EvidencePartitionability;
  /** What the section actually reads, and why that decides the domain. */
  readonly why: string;
}

const G = (why: string): PassportSectionEvidenceRule => ({
  domain: 'identity_general',
  partitionable: 'not_applicable',
  why,
});

/**
 * The declarations. Section ids are the SAME ids `./sectionAudience` uses
 * wherever a section appears in both, because a section is one section
 * wherever it is drawn.
 */
export const PASSPORT_SECTION_EVIDENCE: Readonly<
  Record<string, PassportSectionEvidenceRule>
> = {
  // ── Identity, and the application record ────────────────────────────────
  'employment-history': G('CORE-D employment record: where and when, not what kind of work.'),
  'role-tracks': G(
    '`membership_role_events` — the roles this person has HELD over time. It is ' +
    'the history of every capacity at once, so narrowing it to one lens would ' +
    'hide the very transitions the lens switcher exists to explain.',
  ),
  'certifications': G('Person-authored credentials on `passport_certifications`.'),
  'experience': G('Person-authored work history.'),
  'education': G('Person-authored education.'),
  'references': G('Person-authored references.'),
  'eligibility': G('Person-authored work eligibility.'),
  'toolbox': G("The person's own tools. Owned by them, not measured by a shop."),
  'school': G('School enrolment, gated on the student Passport identity role.'),
  'student-verification-summary': G('Instructor-verified student competencies.'),
  'connect-visibility': G(
    "The person's own discoverability switch over their portable identity record. " +
    'Shop work roles do not reach it and it does not reach them — see the Phase 8 proof.',
  ),

  // ── Mechanical technician evidence ──────────────────────────────────────
  'proficiencies': {
    domain: 'technician_evidence',
    partitionable: 'frozen_role',
    why:
      'CORE-E canonical proficiency categories. `core_e_ticket_performer_categories` ' +
      'excludes DETAIL-family work at source, stated against the FAMILY rather than ' +
      'left to rest on the catalogue not containing a detail category. A detailer ' +
      'therefore has no fabricated mechanical score — they have none.',
  },
  'skill-heatmap': {
    domain: 'technician_evidence',
    partitionable: 'frozen_role',
    why: 'CORE-F projection of the same CORE-E proficiency categories.',
  },
  'diagnostic-competency': {
    domain: 'technician_evidence',
    partitionable: 'frozen_role',
    why: 'Diagnostic competency scored from mechanical diagnosis outcomes.',
  },
  'performance-dna': {
    domain: 'technician_evidence',
    partitionable: 'not_partitioned',
    why:
      'CORE-G efficiency and comeback counts, computed over `tickets` completed by ' +
      "the person's shop technician rows. Tickets carry no performed role, so this " +
      'cannot be split by capacity today and must say so rather than present a ' +
      'mixed number as a technician number.',
  },
  'inspection-metrics': {
    domain: 'technician_evidence',
    partitionable: 'not_applicable',
    why: '`ticket_workflow_inspections` book-vs-actual. Inspection is mechanical work.',
  },
  'pensieve-summary': {
    domain: 'technician_evidence',
    partitionable: 'not_partitioned',
    why: 'OptiTime job-time learning coverage, keyed to mechanical job categories.',
  },
  'pensieve-insights': {
    domain: 'technician_evidence',
    partitionable: 'not_partitioned',
    why: 'OptiTime learning detail, same source as the summary.',
  },
  'pensieve-coaching': {
    domain: 'technician_evidence',
    partitionable: 'not_partitioned',
    why: 'OptiTime coaching detail, same source as the summary.',
  },
  'future-insights': {
    domain: 'technician_evidence',
    partitionable: 'frozen_role',
    why:
      'Derived from proficiencies and metrics. It inherits the CORE-E family ' +
      'exclusion from its input and asserts nothing of its own.',
  },
  'learning-center': {
    domain: 'technician_evidence',
    partitionable: 'frozen_role',
    why: 'Training recommendations keyed to CORE-E proficiency categories.',
  },

  // ── Advisor evidence ────────────────────────────────────────────────────
  'advisor-activity': {
    domain: 'advisor_evidence',
    partitionable: 'not_applicable',
    why:
      'Counts FOH advisor ACTIONS by the user who performed them. Activity-based, ' +
      'not title-based — which is exactly why an OWNER + SERVICE_ADVISOR reads ' +
      'their advisor work here and not from their authority.',
  },
  'advisor-snapshot': {
    domain: 'advisor_evidence',
    partitionable: 'not_applicable',
    why: 'Advisor intelligence summary over advisor-performed actions.',
  },
  'advisor-decision-quality': {
    domain: 'advisor_evidence',
    partitionable: 'not_applicable',
    why: 'Quality of advisor decisions; the unit of measure is an advisor decision.',
  },
  'advisor-diagnostic-translation': {
    domain: 'advisor_evidence',
    partitionable: 'not_applicable',
    why: 'How well the advisor translated a diagnosis for a customer.',
  },
  'advisor-workflow-impact': {
    domain: 'advisor_evidence',
    partitionable: 'not_applicable',
    why: 'Effect of advisor actions on shop workflow.',
  },
  'advisor-learning-recommendations': {
    domain: 'advisor_evidence',
    partitionable: 'not_applicable',
    why: 'Training derived from advisor evidence.',
  },

  // ── Detail evidence ─────────────────────────────────────────────────────
  'detail-work-evidence': {
    domain: 'detail_evidence',
    partitionable: 'frozen_role',
    why:
      'Detail work read from the CAPTURED attribution — `performed_work_family = ' +
      "DETAIL` on `ticket_job_performer_evidence`. It reports what was done and " +
      'how long it took. It scores nothing, because nothing in the product scores ' +
      'detail quality yet and inventing a number to fill the page would be worse ' +
      'than an honest empty state.',
  },

  // ── Leadership ──────────────────────────────────────────────────────────
  'manager-snapshot': {
    domain: 'leadership_evidence',
    partitionable: 'not_applicable',
    why: 'Manager intelligence summary. Leadership behaviour, not work performed.',
  },
  'manager-intervention-effectiveness': {
    domain: 'leadership_evidence',
    partitionable: 'not_applicable',
    why: 'Did this leader\'s interventions help? A leadership question.',
  },
  'manager-load-management': {
    domain: 'leadership_evidence',
    partitionable: 'not_applicable',
    why: 'How the leader distributed load across the floor.',
  },
  'manager-coaching-impact': {
    domain: 'leadership_evidence',
    partitionable: 'not_applicable',
    why: 'Effect of the leader\'s coaching on their people.',
  },
  'manager-system-health': {
    domain: 'leadership_evidence',
    partitionable: 'not_applicable',
    why: 'Health of the shop under this leader.',
  },
  'manager-learning-recommendations': {
    domain: 'leadership_evidence',
    partitionable: 'not_applicable',
    why: 'Training derived from leadership evidence.',
  },

  // ── Mixed ───────────────────────────────────────────────────────────────
  'snapshot-card': {
    domain: 'mixed',
    partitionable: 'not_applicable',
    why:
      'Identity and headline summary — and it also renders the technician-type ' +
      'title, which `resolveTechnicianTypeTitle` derives partly from mechanical ' +
      'proficiency. Identity must appear under every lens, so the section stays ' +
      'and the mechanical half is the reason it is `mixed` rather than general.',
  },
  'time-signature': {
    domain: 'mixed',
    partitionable: 'not_partitioned',
    why:
      'Minute productivity: recorded minutes against available minutes. Minutes ' +
      'are minutes whoever earned them, so this is not a mechanical claim. Its ' +
      'source (`useMinuteProductivityMetrics`) does not read the frozen performed ' +
      'role, so it cannot be split by lens today.',
  },
  'ticket-ledger': {
    domain: 'mixed',
    partitionable: 'not_partitioned',
    why:
      'CORE-J: tickets this person COMPLETED, with job names and categories. Work ' +
      'done is work done in any capacity. It joins `tickets.completed_by_tech_id`, ' +
      'not the allocation rows, so the frozen performed role is not reachable from ' +
      'it without a different query.',
  },
  'connect-work-ledger': {
    domain: 'mixed',
    partitionable: 'not_partitioned',
    why:
      'Core-100 R5 CORE-J: the person-controlled Connect projection of the same work ledger ' +
      '(connect_work_ledger, default OFF). Work done in any capacity, like the ledger it projects.',
  },
  'coaching-notes': {
    domain: 'mixed',
    partitionable: 'not_applicable',
    why: 'Free-text manager notes about the person. Their subject is not constrained.',
  },
};

/**
 * Sections that render in every lens, for whichever reason.
 *
 * `mixed` is here with `identity_general` deliberately. A mixed section
 * measures something real that spans capacities; hiding it under a lens would
 * remove a true fact from the person's own record in order to tidy a page.
 */
const ALWAYS_VISIBLE_DOMAINS: ReadonlySet<PassportEvidenceDomain> = new Set([
  'identity_general',
  'mixed',
]);

const DOMAIN_FOR_LENS: Readonly<Record<PassportWorkLens, PassportEvidenceDomain>> = {
  TECHNICIAN: 'technician_evidence',
  SERVICE_ADVISOR: 'advisor_evidence',
  DETAIL_SPECIALIST: 'detail_evidence',
};

/**
 * May this section be drawn while the person is read through this lens?
 *
 * ── `lens === null` MEANS NO FILTER, AND THAT IS THE IMPORTANT CASE ────────
 *
 * The first draft read `null` as "this person has no capacity, so show them no
 * work evidence". That is wrong, and it is wrong in the direction that does
 * real damage.
 *
 * A lens narrows the page to ONE capacity so that mechanical proficiency is
 * not presented as evidence about washing cars. That misattribution can only
 * happen when a capacity is actually being claimed. With no lens selected,
 * nothing is being claimed — and hiding the person's recorded proficiencies
 * anyway does not make the page more truthful, it deletes facts from their own
 * professional record. CORE-C's self-view floor exists to forbid exactly that.
 *
 * The case that makes it concrete is not exotic, it is the common one. A
 * technician in a MIGRATED shop carries `memberships.role = 'TECHNICIAN'` and
 * has no `membership_work_roles` rows at all, so the set resolves EMPTY with
 * `workRolesStated: false` — unknown, not none. Reading that as "no lens, so
 * no work evidence" would blank the Passport of every technician in every shop
 * that has not yet been through the role editor.
 *
 * So: no lens ⇒ the page renders as it did before lenses existed.
 *
 * Leadership stays out regardless. It is not part of the work-lens
 * composition on any surface — `ManagerPassport` gates the whole page — and
 * admitting it here would only make "leadership is never reachable from a work
 * lens" a thing this function sometimes means.
 *
 * ── AND IT FAILS OPEN ON AN UNKNOWN SECTION ID ────────────────────────────
 * See the header: a truthfulness filter over the subject's own page. The
 * disclosure boundary is `isSectionVisibleToAudience`, which fails closed.
 */
export function isSectionTruthfulForLens(
  sectionId: string,
  lens: PassportWorkLens | null,
): boolean {
  const rule = PASSPORT_SECTION_EVIDENCE[sectionId];
  if (!rule) return true;
  // Leadership is never reachable from a work-lens composition, filtered or
  // not, because no work-lens surface composes it.
  if (rule.domain === 'leadership_evidence') return false;
  // No capacity is being claimed, so nothing can be misattributed to one.
  if (!lens) return true;
  if (ALWAYS_VISIBLE_DOMAINS.has(rule.domain)) return true;
  return rule.domain === DOMAIN_FOR_LENS[lens];
}

/**
 * The limitation note a section must carry under a lens, or `null`.
 *
 * Returned rather than rendered so the caller controls placement, and so the
 * exact wording is written once. A `mixed` section always carries one under a
 * lens, because "these minutes include your detail work" is the thing a reader
 * would otherwise assume the opposite of.
 */
export function lensLimitationNote(
  sectionId: string,
  lens: PassportWorkLens | null,
): string | null {
  const rule = PASSPORT_SECTION_EVIDENCE[sectionId];
  if (!rule || !lens) return null;
  if (rule.partitionable !== 'not_partitioned') return null;
  return 'Not split by work role — this figure covers all of this person\'s recorded work, not only their work as a ' +
    `${lens === 'TECHNICIAN' ? 'technician' : lens === 'SERVICE_ADVISOR' ? 'service advisor' : 'detail specialist'}.`;
}

/** Every section id this table declares. Used by the exhaustiveness test. */
export const DECLARED_EVIDENCE_SECTION_IDS: readonly string[] =
  Object.keys(PASSPORT_SECTION_EVIDENCE);
